import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Clock,
  Loader2,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, getStatusBadgeVariant } from "../components/ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { apiService } from "../services/api.service";
import type { SkillCheckAdminItem, SkillCheckStats } from "../types/admin.types";

/**
 * 期間フィルタの選択肢
 * DASH-008: 統計期間をフィルタできる（7日/30日/全期間）
 */
type PeriodFilter = "7days" | "30days" | "all";

const PERIOD_FILTER_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: "7days", label: "7日間" },
  { value: "30days", label: "30日間" },
  { value: "all", label: "全期間" },
];

/**
 * 期間フィルタから日付範囲を計算
 */
function getPeriodDateRange(period: PeriodFilter): { since?: string; until?: string } {
  const now = new Date();
  const until = now.toISOString();

  switch (period) {
    case "7days": {
      const since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      return { since, until };
    }
    case "30days": {
      const since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      return { since, until };
    }
    case "all":
      return {};
  }
}

export function DashboardPage() {
  const [stats, setStats] = useState<SkillCheckStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<SkillCheckAdminItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodFilter>("30days");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const dateRange = getPeriodDateRange(period);
        const [statsData, jobsData] = await Promise.all([
          apiService.getStats(dateRange.since, dateRange.until),
          apiService.listSkillChecks({}, 10, 0),
        ]);
        setStats(statsData);
        setRecentJobs(jobsData.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : "データの取得に失敗しました");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [period]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー with 期間フィルタ (DASH-008) */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-bold text-2xl text-gray-900">ダッシュボード</h1>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-sm">期間:</span>
          <div className="flex rounded-lg border border-gray-200 bg-white p-1">
            {PERIOD_FILTER_OPTIONS.map((option) => (
              <button
                className={`rounded-md px-3 py-1 font-medium text-sm transition-colors ${
                  period === option.value
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                key={option.value}
                onClick={() => setPeriod(option.value)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 統計カード (DASH-001, DASH-002, DASH-003, DASH-004) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-blue-100 p-3">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">総スキルチェック数</p>
              <p className="font-bold text-2xl">{stats?.total ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">成功</p>
              <p className="font-bold text-2xl">{stats?.completed ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-red-100 p-3">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">失敗</p>
              <p className="font-bold text-2xl">{stats?.failed ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-yellow-100 p-3">
              <Loader2 className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">処理中/待機中</p>
              <p className="font-bold text-2xl">{stats?.pending ?? 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 py-6">
            <div className="rounded-full bg-purple-100 p-3">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">平均処理時間</p>
              <p className="font-bold text-2xl">
                {stats?.averageProcessingTimeMs
                  ? `${(stats.averageProcessingTimeMs / 1000).toFixed(1)}s`
                  : "-"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 成功率 */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>成功率</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="h-4 flex-1 rounded-full bg-gray-200">
                <div
                  className="h-4 rounded-full bg-green-500 transition-all duration-500"
                  style={{ width: `${stats.successRate * 100}%` }}
                />
              </div>
              <span className="font-semibold text-lg">{(stats.successRate * 100).toFixed(1)}%</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* マッチレベル分布とエラーコード分布 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* マッチレベル分布 (DASH-005) */}
        <Card>
          <CardHeader>
            <CardTitle>マッチレベル分布</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.matchLevelDistribution && stats.matchLevelDistribution.length > 0 ? (
              <div className="flex h-40 items-end gap-2">
                {[1, 2, 3, 4, 5].map((level) => {
                  const item = stats.matchLevelDistribution.find((d) => d.level === level);
                  const count = item?.count ?? 0;
                  const maxCount = Math.max(...stats.matchLevelDistribution.map((d) => d.count), 1);
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;

                  return (
                    <div className="flex flex-1 flex-col items-center" key={level}>
                      <span className="mb-1 text-gray-600 text-sm">{count}</span>
                      <div
                        className={`w-full rounded-t transition-all duration-500 ${
                          level >= 4
                            ? "bg-green-500"
                            : level >= 3
                              ? "bg-blue-500"
                              : level >= 2
                                ? "bg-yellow-500"
                                : "bg-red-500"
                        }`}
                        style={{ height: `${height}%`, minHeight: count > 0 ? "4px" : "0" }}
                      />
                      <span className="mt-1 font-medium text-sm">Lv.{level}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center text-gray-500">
                データがありません
              </div>
            )}
          </CardContent>
        </Card>

        {/* エラーコード別発生件数表 (DASH-006) */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>エラーコード別発生件数</CardTitle>
            <Link
              className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
              to="/admin/errors"
            >
              詳細を見る <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.errorCodeDistribution && stats.errorCodeDistribution.length > 0 ? (
              <div className="space-y-2">
                {stats.errorCodeDistribution.map((item) => {
                  const totalErrors = stats.errorCodeDistribution.reduce(
                    (sum, e) => sum + e.count,
                    0,
                  );
                  const percentage = totalErrors > 0 ? (item.count / totalErrors) * 100 : 0;

                  return (
                    <div className="flex items-center gap-3" key={item.code}>
                      <div className="flex w-48 items-center gap-2 truncate">
                        <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
                        <span className="truncate font-mono text-xs" title={item.code}>
                          {item.code}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="h-2 rounded-full bg-gray-200">
                          <div
                            className="h-2 rounded-full bg-red-400 transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-16 text-right font-medium text-gray-700 text-sm">
                        {item.count}件
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  エラーはありません
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 直近のジョブ */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>直近のスキルチェック</CardTitle>
          <Link
            className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
            to="/admin/skill-checks"
          >
            すべて表示 <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-gray-200">
            {recentJobs.map((job) => (
              <Link
                className="-mx-6 flex items-center justify-between px-6 py-3 transition-colors hover:bg-gray-50"
                key={job.id}
                to={`/admin/skill-checks/${job.id}`}
              >
                <div className="flex items-center gap-4">
                  <Badge variant={getStatusBadgeVariant(job.status)}>{job.status}</Badge>
                  <div>
                    <p className="font-medium text-gray-900">{job.candidateName ?? "名前未取得"}</p>
                    <p className="text-gray-500 text-sm">{job.jobPostingTitle}</p>
                  </div>
                </div>
                <div className="text-right">
                  {job.overallMatchLevel !== null && (
                    <p className="font-medium">Lv.{job.overallMatchLevel}</p>
                  )}
                  <p className="text-gray-500 text-sm">
                    {new Date(job.createdAt).toLocaleString("ja-JP")}
                  </p>
                </div>
              </Link>
            ))}
            {recentJobs.length === 0 && (
              <p className="py-4 text-center text-gray-500">スキルチェックデータがありません</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
